import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, BarChart3, FileText, Users, Search, ExternalLink, Download, LogOut, Archive, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [responses, setResponses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [adminUser, setAdminUser] = useState("");
  const [archives, setArchives] = useState([]);
  const [archiving, setArchiving] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  useEffect(() => {
    // Check if admin is logged in
    const token = sessionStorage.getItem('adminToken');
    const user = sessionStorage.getItem('adminUser');
    if (!token) {
      navigate('/admin');
      return;
    }
    setAdminUser(user || 'Admin');
    loadData();
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUser');
    toast.success("Logged out successfully");
    navigate('/admin');
  };

  const loadData = async () => {
    try {
      const [policiesRes, responsesRes, archivesRes] = await Promise.all([
        fetch(`${API}/policies`),
        fetch(`${API}/survey/responses`),
        fetch(`${API}/responses/archives`)
      ]);
      
      const policiesData = await policiesRes.json();
      const responsesData = await responsesRes.json();
      const archivesData = await archivesRes.json();
      
      setPolicies(policiesData);
      setResponses(responsesData);
      setArchives(archivesData);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load data");
    }
  };

  const handleArchiveAll = async () => {
    setArchiving(true);
    try {
      const res = await fetch(`${API}/responses/archive`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Archived ${data.archived_count} responses`);
        setShowArchiveConfirm(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to archive");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while archiving");
    } finally {
      setArchiving(false);
    }
  };

  const handleExportArchive = async (batchId) => {
    try {
      const response = await fetch(`${API}/responses/archives/${batchId}/export`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `archive_${batchId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Archive exported successfully!");
      } else {
        toast.error("Failed to export archive");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    }
  };

  const getPolicyResponseCount = (policyId) => {
    return responses.filter(r => r.policy_id === policyId).length;
  };

  const handleExport = async (policyId) => {
    try {
      const response = await fetch(`${API}/export/${policyId}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `policy_${policyId}_data.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Data exported successfully!");
      } else {
        toast.error("Failed to export data");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    }
  };

  // Get unique categories
  const categories = ["all", ...new Set(policies.map(p => p.category).filter(Boolean))];

  // Filter policies
  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = 
      policy.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      policy.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || policy.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const stats = [
    {
      icon: <FileText className="w-6 h-6" />,
      label: "Total Policies",
      value: policies.length,
      testId: "total-policies-stat"
    },
    {
      icon: <Users className="w-6 h-6" />,
      label: "Total Responses",
      value: responses.length,
      testId: "total-responses-stat"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      label: "Avg Responses/Policy",
      value: policies.length > 0 ? Math.round(responses.length / policies.length) : 0,
      testId: "avg-responses-stat"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-transparent to-primary/10 py-12">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <Button 
            data-testid="back-to-home-button"
            variant="ghost" 
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              Logged in as <span className="font-medium text-foreground">{adminUser}</span>
            </span>
            <Button 
              data-testid="logout-button"
              variant="outline"
              size="sm"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="mb-12">
          <h1 className="text-5xl font-bold tracking-tight mb-4">Admin Dashboard</h1>
          <p className="text-xl text-muted-foreground">View and export survey responses by policy</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {stats.map((stat, idx) => (
            <Card key={idx} data-testid={stat.testId} className="border border-border/50 shadow-sm rounded-xl">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  {stat.icon}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-3xl font-bold font-mono">{stat.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Archive Confirmation Modal */}
        {showArchiveConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="archive-confirm-modal">
            <Card className="max-w-md w-full rounded-xl">
              <CardHeader>
                <CardTitle className="text-xl">Archive All Responses?</CardTitle>
                <CardDescription>
                  This will save all <span className="font-semibold text-foreground">{responses.length}</span> current responses to an archive and clear the active data so you can start fresh. Archived data can still be exported as CSV.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex gap-3 justify-end">
                <Button 
                  data-testid="archive-cancel-button"
                  variant="outline" 
                  onClick={() => setShowArchiveConfirm(false)}
                  disabled={archiving}
                >
                  Cancel
                </Button>
                <Button 
                  data-testid="archive-confirm-button"
                  onClick={handleArchiveAll}
                  disabled={archiving}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {archiving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4 mr-2" />
                      Yes, Archive All
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Archive Action Bar */}
        <div className="flex items-center justify-between mb-8 p-4 bg-secondary/30 rounded-xl border border-border/50">
          <div className="flex items-center gap-3">
            <Archive className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium">Data Management</p>
              <p className="text-xs text-muted-foreground">
                {responses.length} active responses &middot; {archives.length} archived batches
              </p>
            </div>
          </div>
          <Button
            data-testid="archive-all-button"
            onClick={() => setShowArchiveConfirm(true)}
            disabled={responses.length === 0}
            variant="outline"
            className="border-amber-600/50 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
          >
            <Archive className="w-4 h-4 mr-2" />
            Archive All Responses
          </Button>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <Input
              data-testid="policy-search-input"
              type="text"
              placeholder="Search policies by title or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-6">
          <TabsList className="flex-wrap h-auto gap-2 bg-secondary/50 p-2">
            {categories.map(category => (
              <TabsTrigger 
                key={category} 
                value={category}
                data-testid={`category-tab-${category}`}
                className="capitalize"
              >
                {category === "all" ? "All Categories" : category}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Policies List */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-3xl font-semibold tracking-tight">Policies & Responses</h2>
            <span className="text-sm text-muted-foreground" data-testid="filtered-count">
              Showing {filteredPolicies.length} of {policies.length} policies
            </span>
          </div>
          
          {filteredPolicies.length === 0 ? (
            <Card className="border border-border/50 rounded-xl">
              <CardContent className="p-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg text-muted-foreground mb-2">No policies found</p>
                <p className="text-sm text-muted-foreground">Try adjusting your search or filters</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {filteredPolicies.map((policy) => {
                const responseCount = getPolicyResponseCount(policy.id);
                return (
                  <Card 
                    key={policy.id} 
                    data-testid={`policy-card-${policy.id}`}
                    className="border border-border/50 shadow-sm hover:shadow-md transition-all rounded-xl"
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-medium px-2 py-1 bg-primary/10 text-primary rounded-md">
                              {policy.category || "General"}
                            </span>
                            {policy.date_effective && (
                              <span className="text-xs text-muted-foreground">
                                Effective: {policy.date_effective}
                              </span>
                            )}
                          </div>
                          <CardTitle className="text-2xl mb-2">
                            {policy.url ? (
                              <a 
                                href={policy.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="hover:text-primary transition-colors inline-flex items-center gap-2"
                                data-testid={`policy-title-link-${policy.id}`}
                              >
                                {policy.title}
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            ) : (
                              policy.title
                            )}
                          </CardTitle>
                          <CardDescription className="text-base mb-2">{policy.description}</CardDescription>
                          {policy.source && (
                            <p className="text-xs text-muted-foreground italic">
                              Source: {policy.source}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2 ml-4">
                          <span className="text-sm text-muted-foreground font-mono" data-testid={`response-count-${policy.id}`}>
                            {responseCount} {responseCount === 1 ? 'response' : 'responses'}
                          </span>
                          <div className="flex gap-2">
                            <Button 
                              data-testid={`view-responses-button-${policy.id}`}
                              onClick={() => navigate(`/analysis/${policy.id}`)}
                              size="sm"
                              variant="outline"
                              disabled={responseCount === 0}
                            >
                              <BarChart3 className="w-4 h-4 mr-2" />
                              View Charts
                            </Button>
                            <Button 
                              data-testid={`export-button-${policy.id}`}
                              onClick={() => handleExport(policy.id)}
                              size="sm"
                              className="bg-primary hover:bg-primary/90"
                              disabled={responseCount === 0}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Export CSV
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Archive History */}
        {archives.length > 0 && (
          <div className="mt-12 space-y-6">
            <h2 className="text-3xl font-semibold tracking-tight">Archived Batches</h2>
            <div className="grid grid-cols-1 gap-4">
              {archives.map((archive) => (
                <Card 
                  key={archive.batch_id} 
                  data-testid={`archive-card-${archive.batch_id}`}
                  className="border border-border/50 rounded-xl"
                >
                  <CardContent className="p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Archive className="w-5 h-5 text-amber-700" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {archive.response_count} responses archived
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(archive.archived_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'long', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </p>
                      </div>
                    </div>
                    <Button
                      data-testid={`export-archive-${archive.batch_id}`}
                      onClick={() => handleExportArchive(archive.batch_id)}
                      size="sm"
                      variant="outline"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
